/**
 * Route guard for role-restricted pages.
 *   - While auth state is loading, renders a centered loader.
 *   - If the user is unauthenticated, redirects to `/login` (preserving the
 *     attempted location so we can send them back after sign-in).
 *   - If the user is signed in but their profile failed to load (role is
 *     null), shows a recoverable error rather than bouncing them onto the
 *     public landing page in a silently-broken state.
 *   - If the user is authenticated with a role that isn't in `allow`, sends
 *     them to their role's home: baristas go to `/kitchen`, everyone else
 *     to `/`.
 */
import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import type { UserRole } from '@cracked-mug/shared';
import { useAuthStore } from '../store/authStore';

export interface RoleGuardProps {
  allow: UserRole[];
  children: ReactNode;
}

export function RoleGuard({ allow, children }: RoleGuardProps) {
  const location = useLocation();
  const isLoading = useAuthStore((s) => s.isLoading);
  const firebaseUser = useAuthStore((s) => s.firebaseUser);
  const role = useAuthStore((s) => s.role);
  const error = useAuthStore((s) => s.error);

  if (isLoading) {
    return (
      <div className="min-h-dvh flex items-center justify-center">
        <span className="font-mono text-sm text-wca-gray-mid">Loading…</span>
      </div>
    );
  }

  if (!firebaseUser) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Signed in, but the profile bootstrap failed (network blip / API cold
  // start) so we have no role. Don't silently redirect to `/` — the landing
  // page won't bounce a role-less user, leaving them stranded on the public
  // marketing page while actually logged in. Show a retryable error instead.
  if (role === null) {
    return <AccountLoadError message={error} />;
  }

  if (!allow.includes(role)) {
    const fallback = role === 'barista' ? '/kitchen' : '/';
    return <Navigate to={fallback} replace />;
  }

  return <>{children}</>;
}

/** Recoverable "we couldn't load your account" screen with a retry. A reload
 *  re-runs the Firebase auth listener and re-attempts the profile bootstrap. */
function AccountLoadError({ message }: { message: string | null }) {
  return (
    <div className="flex min-h-dvh flex-col items-center justify-center gap-5 px-6 text-center">
      <div>
        <h1 className="font-display text-2xl text-wca-black">
          We couldn&rsquo;t load your account
        </h1>
        <p className="mx-auto mt-2 max-w-sm text-sm text-wca-gray-mid">
          {message ?? 'Something went wrong loading your profile.'} This is usually a brief network
          hiccup — try again.
        </p>
      </div>
      <button
        type="button"
        onClick={() => window.location.reload()}
        className="rounded-full bg-wca-red px-6 py-3 text-sm font-semibold text-white shadow-[0_8px_20px_-6px_rgba(196,18,48,0.5)] transition-all hover:bg-wca-red-dark active:scale-[0.98]"
      >
        Try again
      </button>
    </div>
  );
}
